"use client";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { persistOAuthParams } from "@/Utils/Other/OAuthRedirect";

function ConsentContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [loading, setLoading] = useState(false);

    const nextUrl = searchParams.get("next");
    const clientId = searchParams.get("client_id");

    useEffect(() => {
        const accessToken = Cookies.get("bat") || Cookies.get("iat") || Cookies.get("aat") || Cookies.get("verifyUAT");
        const query = searchParams.toString();

        if (accessToken || !query) {
            return;
        }

        persistOAuthParams(`?${query}`);
        router.replace(`/login?${query}`);
    }, [router, searchParams]);

    // Map client_id to a friendly app name
    const appName = "ChatGPT";

    const handleAllow = () => {
        setLoading(true);
        if (nextUrl) {
            try {
                const url = new URL(nextUrl);
                // Get the access token from cookies
                const accessToken = Cookies.get("bat") || Cookies.get("iat") || Cookies.get("aat") || Cookies.get("verifyUAT");

                if (accessToken) {
                    url.searchParams.set("access_token", accessToken);
                }

                window.location.href = url.toString();
            } catch (e) {
                console.log("Invalid URL:", e);
                window.location.href = nextUrl;
            }
        }
    };

    const handleDeny = () => {
        window.location.href = "https://chatgpt.com";
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-5 sm:px-8">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center p-2">
                                <Image
                                    src="/assets/images/bee-logo.png"
                                    alt="Connector Club"
                                    width={28}
                                    height={28}
                                    className="h-7 w-7 object-contain"
                                />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500">From</p>
                                <p className="text-sm font-semibold text-slate-800">Connector Club</p>
                            </div>
                        </div>
                        <svg className="h-5 w-5 text-slate-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h9.586L10.293 5.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 11H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-lg bg-emerald-50 flex items-center justify-center">
                               <Image src="/assets/images/chatgpt.jpg" alt="Connector Club" width={28} height={28} className="h-7 w-7 object-contain" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500">To</p>
                                <p className="text-sm font-semibold text-slate-800">{appName}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-7 sm:px-8">
                    <h1 className="text-2xl font-bold text-slate-900">Authorize {appName}</h1>
                    <p className="text-slate-600 mt-2">
                        <span className="font-semibold">{appName}</span> wants permission to access your Connector Club account.
                    </p>

                    <div className="mt-6 bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <p className="text-sm font-semibold text-slate-800 mb-3">This will allow {appName} to:</p>
                        <ul className="space-y-2.5">
                            <li className="flex items-center text-sm text-slate-600">
                                <svg className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                View your basic profile information
                            </li>
                            <li className="flex items-center text-sm text-slate-600">
                                <svg className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Access your campaign and account context
                            </li>
                            <li className="flex items-center text-sm text-slate-600">
                                <svg className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Continue secure sign-in using your current session
                            </li>
                        </ul>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button
                            onClick={handleDeny}
                            className="flex-1 py-3 px-4 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                        >
                            Deny
                        </button>
                        <button
                            onClick={handleAllow}
                            disabled={loading}
                            className="flex-1 py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                            {loading ? "Redirecting..." : "Allow Access"}
                        </button>
                    </div>

                    {/* <p className="text-xs text-slate-400 text-center mt-4">
                        You can revoke access later from your Connector Club account settings.
                    </p> */}
                </div>
            </div>
        </div>
    );
}

export default function OAuthConsentPage() {
    return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
            <ConsentContent />
        </Suspense>
    );
}